import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api/core';
import { showErrorToast } from '@/lib/toast';

const MAX_BODY = 2000;

// Shared between the player (086) and director (087) thread views. Each surface
// supplies its own namespaced labels so the strings stay in the right i18n ns.
export interface MessageComposerLabels {
  placeholder: string;
  send: string;
  sending: string;
  sendError: string;
}

export function MessageComposer({
  onSend,
  labels,
}: {
  onSend: (body: string) => Promise<void>;
  labels: MessageComposerLabels;
}) {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);

  async function handleSend() {
    const body = value.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await onSend(body);
      setValue('');
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(labels.sendError);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex items-end gap-2 border-t bg-card p-3">
      <textarea
        data-testid="message-composer-input"
        className="min-h-[2.5rem] flex-1 resize-none rounded-md border bg-background px-3 py-2 text-sm"
        rows={1}
        maxLength={MAX_BODY}
        placeholder={labels.placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <Button
        data-testid="message-send-button"
        disabled={sending || value.trim().length === 0}
        onClick={handleSend}
      >
        {sending ? labels.sending : labels.send}
      </Button>
    </div>
  );
}
